import React, { useState, useEffect, useMemo, useContext } from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import { PhGridPagination, PhGridFilter, PhGridSort } from 'components/PhGrid';
import { API } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { toast } from 'react-toastify';
import { InlineSpinner } from "components/Spinner";
import { sortAlphabetical, findArrayIndex } from "util/dashboardManagement";
import modalStyles from "assets/styles/modal.module.scss";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactComponent as ResetIcon } from 'assets/img/reset_icon.svg';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import {
    faCheck,
    faInfoCircle,
    faXmark,
    faThumbTack,
    faThumbTackSlash,
    faChartColumn,
    faShareNodes,
    faTriangleExclamation,
    faPen,
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import classes from './Dashboard.module.scss';
import { LoggedInUserContext } from "contexts/LoggedInUser";
import { Breadcrumbs } from "features/breadcrumb/breadcrumbs";
import { useHistory } from "react-router-dom";

function isDashboardQueryVis(id) {
    return id.includes('dash_query_vis_');
}

function DashboardsContainer() {
    const [showAddDashboard, setShowAddDashboard] = useState(false);
    const getActiveTabFromPath = () => {
        if (typeof window === 'undefined') return 'shared';
        const pathSegments = window.location.pathname.split('/');
        const tabFromUrl = pathSegments[2];
        return ['owned', 'shared'].includes(tabFromUrl) ? tabFromUrl : 'shared';
    };
    const activeTabName = getActiveTabFromPath() === 'shared' ? 'Shared' : 'My';

    const history = useHistory();
    const { loggedInUserProfile } = useContext(LoggedInUserContext);

    const [ownedPaginationOffset, setOwnedPaginationOffset] = useState(0);
    const [ownedPaginationSize, setOwnedPaginationSize] = useState(10);
    const [sharedPaginationOffset, setSharedPaginationOffset] = useState(0);
    const [sharedPaginationSize, setSharedPaginationSize] = useState(10);
    const [filterString, setFilterString] = useState('');
    const [sortColumn, setSortColumn] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('ascending');
    const [dashboardUser, setDashboardUser] = useState(null);
    const [dashboards, setDashboards] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [waitingForDashboards, setWaitingForDashboards] = useState(false);
    const [pinning, setPinning] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [waitingForDashboard, setWaitingForDashboard] = useState(null);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [sharedUsers, setSharedUsers] = useState([]);
    const [sharedAccounts, setSharedAccounts] = useState();
    const [sharedBrands, setSharedBrands] = useState();

    const breadCrumbsItems = useMemo(() => [
        { label: "Home", to: "/" },
        { label: "Dashboards", to: "/dashboards" },
        { label: `${activeTabName} Dashboards`, to: `/dashboards/${getActiveTabFromPath()}` },
    ], [activeTabName]);

    async function getAllDashboards() {
        try {
            const dashboardsPage1 = await API.get('DashboardApi', '/dashboards');

            setDashboards(dashboardsPage1.dashboards.map(dashboard => {
                if (dashboard.owner) {
                    dashboard['ownership'] = 'shared';
                } else {
                    dashboard['ownership'] = 'owned';
                }
                return dashboard;
            }));

            setWaitingForDashboards(false);
            setPinning(null);

            if (dashboardsPage1.pageCount > 1) {
                toast(`First 100 dashboards loaded. Retrieving remaining dashboards in the background`, { type: toast.TYPE.INFO, autoClose: 5000 });
                let dashboardPages = [...dashboardsPage1.dashboards];

                // pages are 0 indexed
                for (let page = 1; page < dashboardsPage1.pageCount; page++) {
                    const nextPage = await API.get('DashboardApi', `/dashboards?page=${page}`);
                    dashboardPages = dashboardPages.concat(nextPage.dashboards);
                }

                dashboardPages.map(dashboard => {
                    if (dashboard.owner) {
                        dashboard['ownership'] = 'shared';
                    } else {
                        dashboard['ownership'] = 'owned';
                    }
                    return dashboard;
                });

                setDashboards(dashboardPages);
                toast(`All dashboards loaded successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
            }
        } catch (err) {
            toast(`There was a problem loading your dashboards`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setWaitingForDashboards(false);
            setPinning(null);
        }
    }

    useEffect(() => {
        async function init() {
            setWaitingForDashboards(true);
            try {
                const dashboardUserData = await API.get('DashboardApi', '/user');

                let dashboardUserPermissions = {
                    "accountAuthorship": [],
                    "brandAuthorship": [],
                    "permissions": 'reader'
                };

                if (dashboardUserData) {
                    dashboardUserPermissions = dashboardUserData;
                    setDashboardUser(dashboardUserPermissions);
                }

                getAllDashboards();

                const userBrands = dashboardUserPermissions.brandAuthorship || [];
                let userAccounts = dashboardUserPermissions.accountAuthorship || [];

                const brandsWithAccounts = [];
                await Promise.allSettled(userBrands?.map(async brandName => {
                    const brandInfo = await API.get('AccountGatewayApi', `/brand/${brandName}`, { withCredentials: true });
                    brandInfo.accounts = sortAlphabetical(brandInfo?.accounts || [], 'name');
                    userAccounts = userAccounts.concat(brandInfo.accounts);
                    return brandInfo;
                })).then(brandPromises => {
                    brandPromises.forEach(brandPromise => {
                        if (brandPromise.status === 'fulfilled') {
                            brandsWithAccounts.push(brandPromise.value);
                        }
                    })
                });

                const userAccountsDeduped = dashboardUserPermissions.permissions === 'author' ? [] : loggedInUserProfile?.accounts;
                userAccounts?.forEach(account => {
                    if (account.uuid) {
                        userAccountsDeduped.push(account);
                    } else if (account.id) {
                        const index = userAccounts.findIndex(item => item.uuid === account.id);
                        if (index === -1) {
                            userAccountsDeduped.push(account);
                        }
                    }
                });

                await Promise.allSettled(userAccountsDeduped.map(async account => {
                    const accountId = account.id || account.uuid;
                    const accountInfo = await API.get('AccountGatewayApi', `/account/${accountId}`, { withCredentials: true });
                    account['users'] = sortAlphabetical(accountInfo?.profiles || [], 'email');
                    return account;
                }));

                if (brandsWithAccounts.length === 0) {
                    setAvailableBrands(['none']);
                } else {
                    setAvailableBrands([...sortAlphabetical(brandsWithAccounts || [], 'name')]);
                }
                if (userAccountsDeduped.length === 0) {
                    setAvailableAccounts(['none']);
                } else {
                    setAvailableAccounts([...sortAlphabetical(userAccountsDeduped || [], 'name')]);
                }
            } catch (err) {
                console.error(err);
                toast(`There was a problem loading data in order to share dashboards`, { type: toast.TYPE.ERROR, autoClose: 5000 });
                setWaitingForDashboards(false);
            }
        }
        init();
    }, []);

    const createDisplayList = useMemo(() => (ownership, offset, size) => {
        if (!dashboards) return [];
        return dashboards
            .filter(dashboard => dashboard.ownership === ownership)
            .filter(dashboard => [
                dashboard.name || '',
                dashboard.description || 'No Description'
            ].join('').toLowerCase().includes(filterString.toLowerCase()))
            .sort((a, b) => {
                if (sortColumn) {
                    if (sortDirection === 'ascending') {
                        return ((a.pinned - b.pinned) || (sortKey(a) > sortKey(b))) ? 1 : ((a.pinned - b.pinned) || (sortKey(a) < sortKey(b))) ? -1 : 0;
                    } else {
                        return ((a.pinned - b.pinned) || (sortKey(a) < sortKey(b))) ? 1 : ((a.pinned - b.pinned) || (sortKey(a) > sortKey(b))) ? -1 : 0;
                    }
                } else {
                    return;
                }
            })
            .slice(offset, offset + size);
    }, [dashboards, filterString, sortColumn, sortKey, sortDirection]);

    const ownedDisplayList = createDisplayList('owned', ownedPaginationOffset, ownedPaginationSize);
    const sharedDisplayList = createDisplayList('shared', sharedPaginationOffset, sharedPaginationSize);

    function displayDashboards(dashboard) {
        return (
            <tr key={dashboard.id}>
                {dashboard.pinned ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardUnpin"
                        placement="top"
                        overlay={<Tooltip>Unpin this dashboard to the remove from the top of your page</Tooltip>}
                    >
                        <button type="button" title="Pinned" className='btn font-weight-bold m-1 px-2 py-1' onClick={() => updatePin(dashboard.id, true)}>
                            {pinning === dashboard.id ? <InlineSpinner /> : <FontAwesomeIcon className={`${classes["dashboard-pin-icon"]}`} size="lg" icon={faThumbTack} />}
                        </button>
                    </OverlayTrigger>
                </td> : <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardPin"
                        placement="top"
                        overlay={<Tooltip>Pin this dashboard to the top of your page</Tooltip>}
                    >
                        <button type="button" title="Unpinned" className='btn font-weight-bold m-1 px-2 py-1' onClick={() => updatePin(dashboard.id, false)}>
                            {pinning === dashboard.id ? <InlineSpinner /> : <FontAwesomeIcon className={`${classes["dashboard-unpin-icon"]}`} size="lg" icon={faThumbTackSlash} />}
                        </button>
                    </OverlayTrigger>
                </td>}
                <td>{dashboard.name || ''}</td>
                <td>{dashboard.description || 'No Description'}</td>
                <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardVisualize"
                        placement="top"
                        overlay={<Tooltip>Visualize this dashboard</Tooltip>}
                    >
                        <label className="col-form-label p-0">
                            {isDashboardQueryVis(dashboard.id)
                                ? <button
                                    type="button"
                                    title="Visualize"
                                    className={'btn font-weight-bold m-1 px-2 py-1'}
                                    onClick={() => {
                                        history.push(`/queryvisualization/${dashboard.id}`, {
                                            dashboards: true,
                                            dashboardName: dashboard.name,
                                            dashboardDescription: dashboard.description,
                                            activeTab: getActiveTabFromPath()
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon className={`${classes["dashboard-icons"]}`} size="lg" icon={faChartColumn} />
                                </button>
                                : <button
                                    type="button"
                                    title="Visualize"
                                    className={'btn font-weight-bold m-1 px-2 py-1'}
                                    onClick={() => {
                                        history.push(`/dashboardVisualization/${dashboard.id}`, {
                                            dashboardName: dashboard.name,
                                            dashboardDescription: dashboard.description,
                                            activeTab: getActiveTabFromPath()
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon className={`${classes["dashboard-icons"]}`} size="lg" icon={faChartColumn} />
                                </button>
                            }
                        </label>
                    </OverlayTrigger>
                </td>
                {!dashboard.owner ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardShare"
                        placement="top"
                        overlay={(dashboard.description && dashboard.updateFrequency && (dashboardUser?.permissions === 'author' || isDashboardQueryVis(dashboard.id))) ? <Tooltip>Share this dashboard with other members in your organization</Tooltip> : (dashboardUser?.permissions !== 'author') ? <Tooltip>Only authors can share dashboards</Tooltip> : <Tooltip>Edit dashboard with required information in order to share</Tooltip>}
                    >
                        <button type="button" title="Share" className='btn font-weight-bold m-1 px-2 py-1' disabled={(dashboard.description && dashboard.updateFrequency && (dashboardUser?.permissions === 'author' || isDashboardQueryVis(dashboard.id))) ? false : true} onClick={() => { getDashboard(dashboard) }}>
                            {waitingForDashboard === dashboard.id ? <InlineSpinner /> : <FontAwesomeIcon className={`${classes["dashboard-icons"]}`} size="lg" icon={faShareNodes} />}
                        </button>
                    </OverlayTrigger>
                </td> : <></>}
                {!dashboard.owner ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardEdit"
                        placement="top"
                        overlay={<Tooltip>Edit your dashboards information</Tooltip>}
                    >
                        <button type="button" title="Edit" className='btn font-weight-bold m-1 px-2 py-1' onClick={() => { setSelectedDashboard(dashboard), setShowEditModal(true) }}>
                            <FontAwesomeIcon className={`${classes["dashboard-icons"]}`} size="lg" icon={faPen} />
                        </button>
                    </OverlayTrigger>
                </td> : <></>}
            </tr>
        )
    }

    async function updatePin(dashboardId, unpin) {
        toast(`${unpin ? 'Unpinning' : 'Pinning'} your dashboard`, { type: toast.TYPE.INFO, autoClose: 5000 });

        try {
            setPinning(dashboardId);
            await API.put('DashboardApi', `/dashboard/${dashboardId}/pin`, {
                body: {
                    "unpin": unpin
                },
            });

            getAllDashboards();
            toast(`Dashboard ${unpin ? 'unpinned' : 'pinned'} successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            toast(`There was a problem ${unpin ? 'unpinning' : 'pinning'} your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setPinning(null);
        }
    }

    async function getDashboard(dashboard) {
        toast(`Retrieving share data for your dashboard`, { type: toast.TYPE.INFO, autoClose: 5000 });
        setWaitingForDashboard(dashboard.id);
        try {
            const dashboardData = await API.get('DashboardApi', `/dashboard/${dashboard.id}`);

            const users = [];
            const accounts = [];
            const brands = [];
            dashboardData?.dashboardPermissionsShared?.map(data => {
                if (data.profile) {
                    users.push(data.profile);
                }
                if (data.account) {
                    const matchingAccount = availableAccounts.find(availableAccount =>
                        data.account.uuid === availableAccount.id
                    );
                    if (matchingAccount) {
                        data.account['brand'] = matchingAccount.brand;
                    }
                    accounts.push(data.account);
                }
                if (data.brand) {
                    brands.push(data.brand);
                }
            })

            setSharedUsers([...sortAlphabetical(users || [], 'email')]);
            setSharedAccounts([...sortAlphabetical(accounts || [], 'name')]);
            setSharedBrands([...sortAlphabetical(brands || [], 'name')]);

            setWaitingForDashboard(null);
            setSelectedDashboard(dashboard)
            setShowShareModal(true);
        } catch (error) {
            console.error(error);
            toast(`There was a problem getting your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setShowShareModal(false);
            setWaitingForDashboard(null);
        }
    }

    return (<>
        <Route exact path="/dashboards">
            <Redirect to="/dashboards/shared" />
        </Route>
        <div className={`dashboard-wrapper ${classes["dashboard-wrapper"]}`}>
            <Breadcrumbs items={breadCrumbsItems} />
            <div className={classes["head-wrapper"]}>
                <div>  
                    <h3 className={classes["heading"]}>{activeTabName} Dashboards</h3>
                    <span className={classes["subtitle"]}>
                        {activeTabName === 'Shared'
                            ? 'View and manage dashboards shared with you by others'
                            : 'Create, view, and manage dashboards'
                        }
                    </span>
                </div>
                {activeTabName === "My" && (
                  <button
                    onClick={() => setShowAddDashboard(true)}
                    className="cta-btn-class"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span className={classes["text"]}>Add Dashboard</span>
                  </button>
                )}
            </div>
            <div className="card">
                <div className="card-body p-0">
                    <div className="row">
                        <div className={`${classes["filter-dashboard-list-wrapper"]} col-5`}>
                            {dashboards && (
                                <Switch>
                                    <Route exact path='/dashboards/owned'>
                                        <PhGridFilter
                                            placeholder="Search dashboards by title, descriptions"
                                            filterString={filterString}
                                            setFilterString={setFilterString}
                                            setPaginationOffset={setOwnedPaginationOffset}
                                        />
                                    </Route>
                                    <Route exact path='/dashboards/shared'>
                                        <PhGridFilter
                                            placeholder="Search dashboards by title, descriptions"
                                            filterString={filterString}
                                            setFilterString={setFilterString}
                                            setPaginationOffset={setSharedPaginationOffset}
                                        />
                                    </Route>
                                    <Route path='/dashboards'>
                                        <Redirect to='/dashboards/shared' />
                                    </Route>
                                </Switch>
                            )}
                        </div>
                    </div>
                    <table className="table table-striped table-responsive" style={{ padding: 0 }}>
                        <thead className={classes["card-header"]}>
                            <tr>
                                <th scope="col">Pin</th>
                                <th scope="col">
                                    <OverlayTrigger
                                        key="dashboardTitle"
                                        placement="top"
                                        overlay={<Tooltip>The name of the dashboard</Tooltip>}
                                    >
                                        <label className="col-form-label p-0">
                                            <PhGridSort
                                                columnLabel='Title'
                                                columnName={d => d?.name}
                                                config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                                            />
                                        </label>
                                    </OverlayTrigger>
                                </th>
                                <th scope="col">
                                    <OverlayTrigger
                                        key="dashboardDescription"
                                        placement="top"
                                        overlay={<Tooltip>The description of the dashboard</Tooltip>}
                                    >
                                        <label className="col-form-label p-0">
                                            <PhGridSort
                                                columnLabel='Description'
                                                columnName={d => d?.description}
                                                config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                                            />
                                        </label>
                                    </OverlayTrigger>
                                </th>
                                <th scope="col">Visualize</th>
                                {getActiveTabFromPath() === 'owned' &&
                                    <>
                                        <th scope="col">Share</th>
                                        <th scope="col">Edit</th>
                                    </>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {!dashboards && <tr><td colSpan="6" className="text-center">{waitingForDashboards && <InlineSpinner />}</td></tr>}
                            <Switch>
                                <Route exact path='/dashboards/owned'>
                                    {ownedDisplayList && ownedDisplayList.map(dashboard => (
                                        <React.Fragment key={dashboard.id}>
                                            {displayDashboards(dashboard)}
                                        </React.Fragment>
                                    ))}
                                </Route>
                                <Route exact path='/dashboards/shared'>
                                    {sharedDisplayList && sharedDisplayList.map(dashboard => (
                                        <React.Fragment key={dashboard.id}>
                                            {displayDashboards(dashboard)}
                                        </React.Fragment>
                                    ))}
                                </Route>
                                <Route path='/dashboards'>
                                    <Redirect to='/dashboards/shared' />
                                </Route>
                            </Switch>
                        </tbody>
                    </table>
                    {dashboards && (
                        <div className={classes["pagination-wrapper"]}>
                            <Switch>
                                <Route exact path='/dashboards/owned'>
                                    <PhGridPagination
                                        fullList={dashboards.filter((dashboard) => { return dashboard.ownership === 'owned' })}
                                        paginationOffset={ownedPaginationOffset}
                                        setPaginationOffset={setOwnedPaginationOffset}
                                        setPaginationSize={setOwnedPaginationSize}
                                        paginationSize={ownedPaginationSize}
                                        filterString={filterString}
                                    />
                                </Route>
                                <Route exact path='/dashboards/shared'>
                                    <PhGridPagination
                                        fullList={dashboards.filter((dashboard) => { return dashboard.ownership === 'shared' })}
                                        paginationOffset={sharedPaginationOffset}
                                        setPaginationOffset={setSharedPaginationOffset}
                                        setPaginationSize={setSharedPaginationSize}
                                        paginationSize={sharedPaginationSize}
                                        filterString={filterString}
                                    />
                                </Route>
                                <Route path='/dashboards'>
                                    <Redirect to='/dashboards/shared' />
                                </Route>
                            </Switch>
                        </div>
                    )}
                </div>
            </div>
            <Modal
                size="xl"
                show={showShareModal}
                onHide={() => {
                    setShowShareModal(false);
                }}
                className={modalStyles.modalWrapper}
            >
                <Modal.Header className={`card-header ${modalStyles.modalHeader}`}>
                    <Modal.Title className={modalStyles.modalTitle}>
                        Share Dashboard
                    </Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setShowShareModal(false)}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <DashboardSharing
                        dashboard={selectedDashboard}
                        setShowShareModal={setShowShareModal}
                        availableBrands={availableBrands}
                        availableAccounts={availableAccounts}
                        previouslySharedUsers={sharedUsers}
                        previouslySharedAccounts={sharedAccounts}
                        previouslySharedBrands={sharedBrands}
                    />
                </Modal.Body>
            </Modal>
            <Modal
                size="lg"
                show={showEditModal}
                onHide={() => { setShowEditModal(false) }}
                className={modalStyles.modalWrapper}
            >
                <Modal.Header className={`card-header ${modalStyles.modalHeader}`}>
                    <Modal.Title className={modalStyles.modalTitle}>Edit Dashboard</Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setShowEditModal(false)}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <OwnerEdit
                        dashboard={selectedDashboard}
                        getAllDashboards={getAllDashboards}
                        setShowEditModal={setShowEditModal}
                    />
                </Modal.Body>
            </Modal>
            <Modal
                size="lg"
                show={showAddDashboard}
                onHide={() => { setShowAddDashboard(false) }}
                className={modalStyles.modalWrapper}
            >
                <Modal.Header className={`card-header ${modalStyles.modalHeader}`}>
                    <Modal.Title className={modalStyles.modalTitle}>Add Dashboard</Modal.Title>
                     <button
                        type="button"
                        className="close"
                        onClick={() => setShowAddDashboard(false)}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <AddDashboard
                        setShowAddDashboard={setShowAddDashboard}
                        getAllDashboards={getAllDashboards}
                        userPermission={dashboardUser?.permissions}
                    />
                </Modal.Body>
            </Modal>
        </div>
    </>);
};

function renderAvailableBrandCategories(
    availableAccountBrands,
    previouslySharedBrandNames,
    newlyAddedBrands,
    removedBrands,
    setNewlyAddedBrands,
    setRemovedBrands,
    setPreviouslySharedBrandNames,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds
) {
    return availableAccountBrands.map(brand => {
        if (brand.accounts?.length > 0) {
            const brandChecked = (findArrayIndex(previouslySharedBrandNames, brand.name) > -1) || (findArrayIndex(newlyAddedBrands, brand.name) > -1);
            return (
                <div key={brand.name}>
                    <details>
                        <summary>
                            <input
                                type="checkbox"
                                name={brand.name}
                                value={brand.name}
                                onChange={e => handleOnChange(
                                    e,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    removedBrands,
                                    setNewlyAddedBrands,
                                    setRemovedBrands,
                                    setPreviouslySharedBrandNames
                                )}
                                checked={brandChecked}
                            />
                            {brand?.name || ''}
                        </summary>
                        <p>{brandChecked
                            ? renderDisabledAccountsCheckboxList(brand)
                            : renderAvailableAccountsCheckboxList(
                                brand,
                                previouslySharedAccountIds,
                                newlyAddedAccounts,
                                removedAccounts,
                                setNewlyAddedAccounts,
                                setRemovedAccounts,
                                setPreviouslySharedAccountIds
                            )
                        }</p>
                    </details>
                </div>
            )
        }
    })
}

function renderDisabledAccountsCheckboxList(brand) {
    return brand.accounts?.map(account => {
        return (
            <span key={account.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={account.name}
                        value={account.id}
                        checked
                        disabled
                    />
                    {account?.name || ''}
                </label>
                <br />
            </span>
        )
    })
}

function renderAvailableAccountsCheckboxList(
    brand,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds
) {
    return brand.accounts?.map(account => {
        return (
            <span key={account.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={account.name}
                        value={account.id}
                        onChange={e => handleOnChange(
                            e,
                            previouslySharedAccountIds,
                            newlyAddedAccounts,
                            removedAccounts,
                            setNewlyAddedAccounts,
                            setRemovedAccounts,
                            setPreviouslySharedAccountIds
                        )}
                        checked={(findArrayIndex(previouslySharedAccountIds, account.id) > -1) || (findArrayIndex(newlyAddedAccounts, account.id) > -1)}
                    />
                    {account?.name || ''}
                </label>
                <br />
            </span>
        )
    })
}

function renderAvailableAccountCategories(
    availableUserAccounts,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds,
    previouslySharedBrandNames,
    newlyAddedBrands,
    previouslySharedUserIds,
    newlyAddedUsers,
    removedUsers,
    setNewlyAddedUsers,
    setRemovedUsers,
    setPreviouslySharedUserIds
) {
    return availableUserAccounts.map(account => {
        if (account.users?.length > 0) {
            const accountId = account.uuid || account.id;
            const accountChecked = (findArrayIndex(previouslySharedAccountIds, accountId) > -1) || (findArrayIndex(newlyAddedAccounts, accountId) > -1);
            const brandChecked = (findArrayIndex(previouslySharedBrandNames, account.brand) > -1) || (findArrayIndex(newlyAddedBrands, account.brand) > -1);
            if (!brandChecked) {
                return (
                    <div key={accountId}>
                        <details>
                            <summary>
                                <input
                                    type="checkbox"
                                    name={account.name}
                                    value={accountId}
                                    onChange={e => handleOnChange(
                                        e,
                                        previouslySharedAccountIds,
                                        newlyAddedAccounts,
                                        removedAccounts,
                                        setNewlyAddedAccounts,
                                        setRemovedAccounts,
                                        setPreviouslySharedAccountIds
                                    )}
                                    checked={accountChecked}
                                />
                                {account?.name || ''}
                            </summary>
                            <p>{accountChecked
                                ? renderDisabledUsersCheckboxList(account)
                                : renderAvailableUsersCheckboxList(
                                    account,
                                    previouslySharedUserIds,
                                    newlyAddedUsers,
                                    removedUsers,
                                    setNewlyAddedUsers,
                                    setRemovedUsers,
                                    setPreviouslySharedUserIds
                                )}
                            </p>
                        </details>
                    </div>
                )
            } else {
                return (
                    <div key={accountId}>
                        <details>
                            <summary>
                                <input
                                    type="checkbox"
                                    name={account.name}
                                    value={accountId}
                                    checked
                                    disabled
                                />
                                {account?.name || ''}
                            </summary>
                            <p>{renderDisabledUsersCheckboxList(account)}</p>
                        </details>
                    </div>
                )
            }
        }
    })
}

function renderDisabledUsersCheckboxList(account) {
    return account.users?.map(user => {
        return (
            <span key={user.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={user.email}
                        value={user.id}
                        checked
                        disabled
                    />
                    {user.email}
                </label>
                <br />
            </span>
        )
    })
}

function renderAvailableUsersCheckboxList(
    account,
    previouslySharedUserIds,
    newlyAddedUsers,
    removedUsers,
    setNewlyAddedUsers,
    setRemovedUsers,
    setPreviouslySharedUserIds
) {
    return account.users?.map(user => {
        return (
            <span key={user.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={user.email}
                        value={user.id}
                        onChange={e => handleOnChange(
                            e,
                            previouslySharedUserIds,
                            newlyAddedUsers,
                            removedUsers,
                            setNewlyAddedUsers,
                            setRemovedUsers,
                            setPreviouslySharedUserIds
                        )}
                        checked={(findArrayIndex(previouslySharedUserIds, user.id) > -1) || (findArrayIndex(newlyAddedUsers, user.id) > -1)}
                    />
                    {user.email}
                </label>
                <br />
            </span>
        )
    })
}

function renderSharedBrandsCheckboxList(
    previouslySharedBrands,
    previouslySharedBrandNames,
    newlyAddedBrands,
    removedBrands,
    setNewlyAddedBrands,
    setRemovedBrands,
    setPreviouslySharedBrandNames
) {
    return previouslySharedBrands.map(brand => (
        <span key={brand.name}>
            <label>
                <input
                    type="checkbox"
                    name={brand.name}
                    value={brand.name}
                    onChange={e => handleOnChange(
                        e,
                        previouslySharedBrandNames,
                        newlyAddedBrands,
                        removedBrands,
                        setNewlyAddedBrands,
                        setRemovedBrands,
                        setPreviouslySharedBrandNames
                    )}
                    checked={(findArrayIndex(previouslySharedBrandNames, brand.name) > -1) || (findArrayIndex(newlyAddedBrands, brand.name) > -1)}
                />
                {brand?.name || ''}
            </label>
            <br />
        </span>
    ))
}

function renderSharedAccountsCheckboxList(
    previouslySharedAccounts,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds
) {
    return previouslySharedAccounts.map(account => (
        <span key={account.uuid}>
            <label>
                <input
                    type="checkbox"
                    name={account.name}
                    value={account.uuid}
                    onChange={e => handleOnChange(
                        e,
                        previouslySharedAccountIds,
                        newlyAddedAccounts,
                        removedAccounts,
                        setNewlyAddedAccounts,
                        setRemovedAccounts,
                        setPreviouslySharedAccountIds
                    )}
                    checked={(findArrayIndex(previouslySharedAccountIds, account.uuid) > -1) || (findArrayIndex(newlyAddedAccounts, account.uuid) > -1)}
                />
                {account?.name || ''} ({account?.brand || ''})
            </label>
            <br />
        </span>
    ))
}

function renderSharedUsersCheckboxList(
    previouslySharedUsers,
    previouslySharedUserIds,
    newlyAddedUsers,
    removedUsers,
    setNewlyAddedUsers,
    setRemovedUsers,
    setPreviouslySharedUserIds
) {
    return previouslySharedUsers.map(user => (
        <span key={user.uuid} className={classes["shared-user-list"]}>
            <label className={classes["shared-user-email"]}>
                <input
                    type="checkbox"
                    name={user.email}
                    value={user.uuid}
                    onChange={e => handleOnChange(
                        e,
                        previouslySharedUserIds,
                        newlyAddedUsers,
                        removedUsers,
                        setNewlyAddedUsers,
                        setRemovedUsers,
                        setPreviouslySharedUserIds
                    )}
                    checked={(findArrayIndex(previouslySharedUserIds, user.uuid) > -1) || (findArrayIndex(newlyAddedUsers, user.uuid) > -1)}
                />
                {user?.email || ''} ({user?.accounts?.length > 0 ? user.accounts[0].name : 'No Associated Account'})
            </label>
            <br />
        </span>
    ))
}

const handleOnChange = (
    e,
    previouslySharedSet,
    newlyAddedSet,
    removedSet,
    setNewlyAddedSet,
    setRemovedSet,
    setPreviouslySharedSet
) => {
    const newItem = e.target.value;
    const newlyAddedSetCopy = [...newlyAddedSet];
    const removedSetCopy = [...removedSet];
    const previouslySharedSetCopy = [...previouslySharedSet];
    const addedIndex = findArrayIndex(newlyAddedSetCopy, newItem);
    const existingIndex = findArrayIndex(previouslySharedSetCopy, newItem);

    if (addedIndex > -1) {
        // if user/account/brand has already been selected to be shared, remove it
        newlyAddedSetCopy.splice(addedIndex, 1);
        setNewlyAddedSet([...newlyAddedSetCopy]);
    } else if (existingIndex > -1) {
        // only add to removed user/account/brand if it was already shared with them
        removedSetCopy.push(newItem);
        setRemovedSet([...removedSetCopy]);
        previouslySharedSetCopy.splice(existingIndex, 1);
        setPreviouslySharedSet([...previouslySharedSetCopy]);
    } else {
        // if user/account/brand has not been selected to be shared until now, add it
        newlyAddedSetCopy.push(newItem);
        setNewlyAddedSet([...newlyAddedSetCopy]);
    }
};

function DashboardSharing({
    dashboard,
    setShowShareModal,
    availableBrands,
    availableAccounts,
    previouslySharedUsers,
    previouslySharedAccounts,
    previouslySharedBrands
}) {
    const [availableAccountBrands, setAvailableAccountBrands] = useState([]);
    const [availableUserAccounts, setAvailableUserAccounts] = useState([]);
    const [waitingForShare, setWaitingForShare] = useState(false);
    const [removedUsers, setRemovedUsers] = useState([]);
    const [removedAccounts, setRemovedAccounts] = useState([]);
    const [removedBrands, setRemovedBrands] = useState([]);
    const [newlyAddedUsers, setNewlyAddedUsers] = useState([]);
    const [newlyAddedAccounts, setNewlyAddedAccounts] = useState([]);
    const [newlyAddedBrands, setNewlyAddedBrands] = useState([]);
    const [previouslySharedUserIds, setPreviouslySharedUserIds] = useState([]);
    const [previouslySharedAccountIds, setPreviouslySharedAccountIds] = useState([]);
    const [previouslySharedBrandNames, setPreviouslySharedBrandNames] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState("");
    const [brandSearchTerm, setBrandSearchTerm] = useState("");

    useEffect(() => {
        if (availableBrands) {
            setAvailableAccountBrands([...availableBrands]);
        }
        if (availableAccounts) {
            setAvailableUserAccounts([...availableAccounts]);
        }
        if (previouslySharedUsers.length > 0) {
            setPreviouslySharedUserIds(previouslySharedUsers.map(user => { return user.uuid }));
        }
        if (previouslySharedAccounts.length > 0) {
            setPreviouslySharedAccountIds(previouslySharedAccounts.map(account => { return account.uuid }));
        }
        if (previouslySharedBrands.length > 0) {
            setPreviouslySharedBrandNames(previouslySharedBrands.map(brand => { return brand.name }));
        }
        setInitialShared();
    }, [availableBrands, availableAccounts, previouslySharedUsers, previouslySharedAccounts, previouslySharedBrands]);

    function setInitialShared() {
        setNewlyAddedAccounts([]);
        setNewlyAddedBrands([]);
        setNewlyAddedUsers([]);
        setRemovedAccounts([]);
        setRemovedBrands([]);
        setRemovedUsers([]);
    }

    async function shareDashboard() {
        setWaitingForShare(true);
        try {
            if (newlyAddedUsers.length > 0 || newlyAddedAccounts.length > 0 || newlyAddedBrands.length > 0) {
                await API.put('DashboardApi', `/dashboard/${dashboard.id}/share`, {
                    body: {
                        "dashboardId": dashboard.id,
                        "userIds": newlyAddedUsers,
                        "accountIds": newlyAddedAccounts,
                        "brandNames": newlyAddedBrands,
                        "revoke": false
                    },
                });
            }
            if (removedUsers.length > 0 || removedAccounts.length > 0 || removedBrands.length > 0) {
                await API.put('DashboardApi', `/dashboard/${dashboard.id}/share`, {
                    body: {
                        "dashboardId": dashboard.id,
                        "userIds": removedUsers,
                        "accountIds": removedAccounts,
                        "brandNames": removedBrands,
                        "revoke": true
                    },
                });
            }
            setWaitingForShare(false);
            setShowShareModal(false);
            toast(`Dashboard shared successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            toast(`There was a problem sharing your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setWaitingForShare(false);
            setShowShareModal(false);
        }
    }

    const filteredBrands = useMemo(() => {
        if (availableAccountBrands[0] === 'none') return ['none'];

        return availableAccountBrands
            .map(brand => {
                const brandMatch = brand.name
                    ?.toLowerCase()
                    .includes(brandSearchTerm);

                const filteredAccounts = brand.accounts?.filter(account =>
                    account.name?.toLowerCase().includes(brandSearchTerm)
                );

                if (brandMatch) {
                    return brand;
                }

                if (filteredAccounts && filteredAccounts.length > 0) {
                    return {
                        ...brand,
                        accounts: filteredAccounts
                    };
                }

                return null;
            })
            .filter(Boolean);

    }, [availableAccountBrands, brandSearchTerm]);

    const filteredAccounts = useMemo(() => {
        if (availableUserAccounts[0] === 'none') return ['none'];

        return availableUserAccounts
            .map(account => {
                const accountMatch = account.name
                    ?.toLowerCase()
                    .includes(userSearchTerm);

                const filteredUsers = account.users?.filter(user =>
                    user.email?.toLowerCase().includes(userSearchTerm)
                );

                if (accountMatch) {
                    return account;
                }

                if (filteredUsers && filteredUsers.length > 0) {
                    return {
                        ...account,
                        users: filteredUsers
                    };
                }

                return null;
            })
            .filter(Boolean);

    }, [availableUserAccounts, userSearchTerm]);

    return (
        <div>
            <div className={`container-fluid ${classes["share-dashboard-wrap"]}`}>
                <div className="row">
                    <div className={`col-md ml-2 ${classes["card-block"]}`}>
                        <b className={classes["title"]}>Available Accounts by Brand</b>
                        <div
                            key={availableBrands}
                            className={classes["card-block-content"]}
                        >
                            <input
                                type="text"
                                placeholder="Search accounts"
                                className={classes["search-input"]}
                                value={brandSearchTerm}
                                onChange={(e) => setBrandSearchTerm(e.target.value.toLowerCase())}
                            />
                            {filteredBrands.length > 0 && filteredBrands[0]?.accounts
                                ? renderAvailableBrandCategories(
                                    filteredBrands,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    removedBrands,
                                    setNewlyAddedBrands,
                                    setRemovedBrands,
                                    setPreviouslySharedBrandNames,
                                    previouslySharedAccountIds,
                                    newlyAddedAccounts,
                                    removedAccounts,
                                    setNewlyAddedAccounts,
                                    setRemovedAccounts,
                                    setPreviouslySharedAccountIds
                                ) : brandSearchTerm
                                    ? "No results found"
                                    : filteredBrands[0] !== "none"
                                        ? <InlineSpinner />
                                        : "You do not have permission to share to brands"
                            }
                        </div>
                    </div>
                    <div className={`col-md ml-2 ${classes["card-block"]}`}>
                        <b className={classes["title"]}>Available Users by Account</b>
                        <div
                            key={availableAccounts}
                            className={classes["card-block-content"]}
                        >
                            <input
                                type="text"
                                placeholder="Search users"
                                className={classes["search-input"]}
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value.toLowerCase())}
                            />
                            {filteredAccounts.length > 0 && filteredAccounts[0]?.users
                                ? renderAvailableAccountCategories(
                                    filteredAccounts,
                                    previouslySharedAccountIds,
                                    newlyAddedAccounts,
                                    removedAccounts,
                                    setNewlyAddedAccounts,
                                    setRemovedAccounts,
                                    setPreviouslySharedAccountIds,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    previouslySharedUserIds,
                                    newlyAddedUsers,
                                    removedUsers,
                                    setNewlyAddedUsers,
                                    setRemovedUsers,
                                    setPreviouslySharedUserIds
                                ) : userSearchTerm
                                    ? "No results found"
                                    : filteredAccounts[0] !== "none"
                                        ? <InlineSpinner />
                                        : "You do not have permission to share to accounts"
                            }
                        </div>
                    </div>
                    <div className={`col-md ml-2 ${classes["card-block"]}`}>
                        <b className={classes["title"]}>Current Dashboard Viewers</b>
                        <div className={classes["card-block-content"]}>
                            <details open>
                                <summary>
                                    <u className={classes["list-item"]}>
                                        Brands <FontAwesomeIcon icon={faCircleQuestion} />
                                    </u>
                                </summary>

                                <p className={classes["list-item-content"]}>
                                    {previouslySharedBrands.length > 0
                                        ? renderSharedBrandsCheckboxList(
                                            previouslySharedBrands,
                                            previouslySharedBrandNames,
                                            newlyAddedBrands,
                                            removedBrands,
                                            setNewlyAddedBrands,
                                            setRemovedBrands,
                                            setPreviouslySharedBrandNames
                                        )
                                        : "Dashboard not currently shared with any brands"}
                                </p>
                            </details>
                            <details open>
                                <summary>
                                    <u className={classes["list-item"]}>
                                        Accounts <FontAwesomeIcon icon={faCircleQuestion} />
                                    </u>
                                </summary>
                                <p className={classes["list-item-content"]}>
                                    {previouslySharedAccounts.length > 0
                                        ? renderSharedAccountsCheckboxList(
                                            previouslySharedAccounts,
                                            previouslySharedAccountIds,
                                            newlyAddedAccounts,
                                            removedAccounts,
                                            setNewlyAddedAccounts,
                                            setRemovedAccounts,
                                            setPreviouslySharedAccountIds
                                        )
                                        : "Dashboard not currently shared with any accounts"}
                                </p>
                            </details>
                            <details open>
                                <summary>
                                    <u className={classes["list-item"]}>
                                        Users <FontAwesomeIcon icon={faCircleQuestion} />{" "}
                                    </u>
                                </summary>
                                <p className={classes["list-item-content"]}>
                                    {previouslySharedUsers.length > 0
                                        ? renderSharedUsersCheckboxList(
                                            previouslySharedUsers,
                                            previouslySharedUserIds,
                                            newlyAddedUsers,
                                            removedUsers,
                                            setNewlyAddedUsers,
                                            setRemovedUsers,
                                            setPreviouslySharedUserIds
                                        )
                                        : "Dashboard not currently shared with any users"}
                                </p>
                            </details>
                        </div>
                    </div>
                </div>
                <div className="d-flex justify-content-end mt-4">
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="sharedashboard-reset-tooltip">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <b>Wait!</b> This clears current selections
                            </Tooltip>
                        }
                    >
                        <Button
                            variant="danger"
                            className={`btn mr-2 ${classes["btn-action"]}  ${classes["reset-btn"]}`}
                            onClick={() => setInitialShared()}
                        >
                            <ResetIcon /> <span>Clear</span>
                        </Button>
                    </OverlayTrigger>
                    <Button
                        variant="secondary"
                        className={`btn mr-2 ${classes["btn-action"]}`}
                        onClick={() => setShowShareModal(false)}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                        <span>Cancel</span>
                    </Button>
                    <Button
                        variant="primary"
                        className={`btn mr-2 ${classes["btn-action"]}`}
                        onClick={() => shareDashboard()}
                    >
                        {waitingForShare ? (
                            <InlineSpinner />
                        ) : (
                            <FontAwesomeIcon icon={faCheck} />
                        )}
                        <span>Save</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}

function validateDescription(dashboardDescription) {
    const rg1 = /^[0-9A-Za-z_.-\s]+$/; // alphanumeric, hyphens, underscores, periods and spaces
    return rg1.test(dashboardDescription) && dashboardDescription.length < 256;
}

function validateEditing(id, description, updateFrequency) {
    if (id === '' || description === '' || updateFrequency === '' || !validateDescription(description)) {
        return false;
    }
    return true;
}

function OwnerEdit({
    dashboard,
    getAllDashboards,
    setShowEditModal
}) {
    const [dashboardDescription, setDashboardDescription] = useState('');
    const [dashboardUpdateFrequency, setDashboardUpdateFrequency] = useState('')
    const [waitingForResponse, setWaitingForResponse] = useState(false);

    useEffect(() => {
        if (dashboard) {
            if (dashboard.description) {
                setDashboardDescription(dashboard.description);
            }
            if (dashboard.updateFrequency) {
                setDashboardUpdateFrequency(dashboard.updateFrequency.toLowerCase());
            }
        }
    }, [dashboard]);

    async function updateDashboard() {
        setWaitingForResponse(true);
        try {
            await API.put('DashboardApi', '/dashboard', {
                body: {
                    "dashboardId": dashboard.id,
                    "updateFrequency": dashboardUpdateFrequency,
                    "description": dashboardDescription
                },
            });
            getAllDashboards();
            setShowEditModal(false);
            setWaitingForResponse(false);
            toast(`Dashboard updated successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000, position: "top-center", });
        } catch (error) {
            toast(`There was a problem updating your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000, position: "top-center", });
            setShowEditModal(false);
            setWaitingForResponse(false);
        }
    }

    return (
        <div className={`container-fluid ${classes["edit-dashboard-wrap"]}`}>
            <div className="row">
                <div className="col-12">
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardTitle"
                            placement="left"
                            overlay={<Tooltip>Name of the Dashboard</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Title:</label>
                        </OverlayTrigger>
                        <div className="col-9 px-0">
                            <input
                                type="text"
                                className={`form-control ${classes["title"]}`}
                                value={dashboard.name}
                                disabled
                                title="Title can only be edited in QuickSight"
                            />
                            <span className={classes["title-info-msg"]}>
                                <FontAwesomeIcon icon={faInfoCircle} /> The dashboard title can't be changed because it uniquely identifies this dashboard.
                            </span>
                        </div>
                    </div>
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardDescription"
                            placement="left"
                            overlay={
                                <Tooltip>Provide a description for the dashboard</Tooltip>
                            }
                        >
                            <label className="col-2 col-form-label">Description:</label>
                        </OverlayTrigger>
                        <textarea
                            className={`form-control col-9 ${dashboardDescription &&
                                !validateDescription(dashboardDescription)
                                ? "is-invalid"
                                : ""
                                }`}
                            value={dashboardDescription}
                            onChange={e => setDashboardDescription(e.target.value)}
                            placeholder="Dashboard Description"
                        />
                        {dashboardDescription &&
                            !validateDescription(dashboardDescription) && (
                                <div className="offset-2 invalid-feedback">
                                    Must be under 256 characters long and may only include
                                    alphanumeric characters, hyphens, underscores, periods and
                                    spaces.
                                </div>
                            )}
                    </div>
                </div>
            </div>
            <div className={classes["modal-footer-wrap"]}>
                <Button
                    variant="secondary"
                    className={`btn mr-2 ${classes["btn-action"]}`}
                    onClick={() => setShowEditModal(false)}
                >
                    <FontAwesomeIcon icon={faXmark} />
                    <span>Cancel</span>
                </Button>
                <Button
                    variant="primary"
                    className={`btn mr-2 ${classes["btn-action"]}`}
                    disabled={
                        !validateEditing(
                            dashboard.id,
                            dashboardDescription,
                            dashboardUpdateFrequency
                        )
                    }
                    onClick={() => updateDashboard()}
                >
                    {waitingForResponse ? <InlineSpinner /> : <FontAwesomeIcon icon={faCheck} />}
                    <span>Save</span>
                </Button>
            </div>
        </div>
    );
}

function AddDashboard({
    setShowAddDashboard,
    getAllDashboards,
    userPermission
}) {
    const isAuthor = userPermission === 'author';
    const [dashboardId, setDashboardId] = useState('');
    const [isQueryVis, setIsQueryVis] = useState(!isAuthor);
    const [dashboardDescription, setDashboardDescription] = useState('');
    const dashboardUpdateFrequency = 'none';
    const [waitingForResponse, setWaitingForResponse] = useState(false);
    const [dashboardIdError, setDashboardIdError] = useState(null);

    function validateUUID(uuid) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    }

    async function addDashboard() {
        setWaitingForResponse(true);

        try {
            const idToUse = isQueryVis ? 'dash_query_vis_' + dashboardId : dashboardId;
            await API.put('DashboardApi', '/dashboard', {
                body: {
                    "dashboardId": idToUse,
                    "updateFrequency": dashboardUpdateFrequency,
                    "description": dashboardDescription
                },
            });
            getAllDashboards();
            setShowAddDashboard(false);
            setWaitingForResponse(false);
            toast(`The dashboard was successfully created.`, {
                type: toast.TYPE.SUCCESS,
                autoClose: 5000,
                position: "top-center",
                style: {
                    width: "fit-content",
                    whiteSpace: "nowrap"
                }
            });
        } catch (error) {
            if (error?.response?.data?.error === 'Dashboard not found.') {
                setDashboardIdError('Dashboard not found. Please verify your ID and Query Visualization checkbox and try again.');
            } else if (error?.response?.status === 403) {
                setDashboardIdError('You do not have permission to add this dashboard. Verify the ID or ask the owner to share the dashboard with you.');
            } else {
                toast(`There was a problem adding your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
                setShowAddDashboard(false);
            }
            setWaitingForResponse(false);
        }
    }

    return (
       <div className={`container-fluid ${classes["add-dashboard-wrap"]}`}>
            <div className="row">
                <div className="col-12">
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardId"
                            placement="left"
                            overlay={<Tooltip>Dashboard ID from QuickSight or Job ID from Query My Files</Tooltip>}
                        >
                            <label className="col-3 col-form-label">ID</label>
                        </OverlayTrigger>
                        <input
                            type="text"
                            className={`form-control col-8 ${dashboardId && (dashboardIdError || !validateUUID(dashboardId)) ? 'is-invalid' : ''}`}
                            value={dashboardId}
                            onChange={(e) => { setDashboardId(e.target.value); setDashboardIdError(null); }}
                        />
                        {(dashboardId && !validateUUID(dashboardId)) && <div className="offset-3 invalid-feedback">
                            Invalid ID. Please only enter valid dashboard or query visualization IDs.
                        </div>}
                        {(dashboardId && dashboardIdError) && <div className="offset-3 invalid-feedback">
                            {dashboardIdError}
                        </div>}
                    </div>
                    <div className="form-group row" style={{ alignItems: 'center' }}>
                        <OverlayTrigger
                            key="isQueryVis"
                            placement="left"
                            overlay={<Tooltip>Check this if your dashboard is a Query Visualization</Tooltip>}
                        >
                            <label className="col-3 col-form-label">Query Visualization</label>
                        </OverlayTrigger>
                        <input
                            type="checkbox"
                            name="isQueryVis"
                            className="form-control"
                            style={{ width: '20px', height: '20px', alignContent: 'center' }}
                            onChange={(e) => { setIsQueryVis(e.target.checked); setDashboardIdError(null); }}
                            checked={isQueryVis}
                            disabled={!isAuthor}
                            title={!isAuthor ? 'Author permissions required to add QuickSight Dashboards' : ''}
                        />
                    </div>
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardDescription"
                            placement="left"
                            overlay={<Tooltip>Provide a description for the dashboard</Tooltip>}
                        >
                            <label className="col-3 col-form-label">Description</label>
                        </OverlayTrigger>
                        <textarea
                            className={`form-control col-8 ${dashboardDescription && !validateDescription(dashboardDescription) ? 'is-invalid' : ''}`}
                            value={dashboardDescription}
                            onChange={(e) => setDashboardDescription(e.target.value)}
                            placeholder="Dashboard Description"
                        />
                        {(dashboardDescription && !validateDescription(dashboardDescription)) && <div className="offset-3 invalid-feedback">
                            Must be under 256 characters long and may only include alphanumeric characters, hyphens, underscores, periods and spaces.
                        </div>}
                    </div>
                </div>
            </div>
            <div className={classes["modal-footer-wrap"]}>
                <Button
                    variant="secondary"
                    className={`btn mr-2 ${classes["btn-action"]}`}
                    onClick={() => setShowAddDashboard(false)}
                >
                    <FontAwesomeIcon icon={faXmark} />
                    <span>Cancel</span>
                </Button>
                <Button
                    variant="primary"
                    className={`btn mr-2 ${classes["btn-action"]}`}
                     disabled={(!validateEditing(dashboardId, dashboardDescription, dashboardUpdateFrequency) || !validateUUID(dashboardId))} onClick={() => addDashboard()}
                >
                    {waitingForResponse ? <InlineSpinner /> : <FontAwesomeIcon icon={faCheck} />}
                    <span>Save</span>
                </Button>
            </div>
        </div>
    );
}

export default DashboardsContainer;
